import slugify from "slugify";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";

import lodash from "lodash";
import Fuse from "fuse.js";
import { TreeNode } from "@/types/tree";

import {
  Control,
  ControlTypes,
  OxyNestedSelector,
  OxySelector
} from "@/oxygen/classes/lib/types";

import { UnitInputValueV } from "@/types/unit";
import { ColorPickerType, GradientColorV } from "@/types/colors";
import { configStore, globalStore, uiStore } from "@/store/store";

import { Variable, VariableType } from "@/oxygen/variables/lib/types";
import { extractVariableName, isValueAGradient } from "@/lib/colors/colors";
import { isDynamicShortcode } from "@/lib/dynamic-data/utils";

export const DEFAULT_VARIABLES_COLLECTION = "Default";

export function getVariables() {
  return globalStore.variables;
}

export function isGlobalVariable(value: unknown) {
  // Try and extract variable uuid from control value
  const maybeVariableId = extractVariableId(value);
  return !!maybeVariableId;
}

export function getGlobalVariableFromInputValue(value: unknown) {
  const maybeVariableId = extractVariableId(value);
  if (maybeVariableId) {
    return getGlobalVariableById(maybeVariableId);
  }

  const maybeCssVariable = returnCssVariableOrNothing(value);
  if (maybeCssVariable) {
    return getGlobalVariableByCssName(maybeCssVariable);
  }

  return undefined;
}

/**
 * Unit values are nested in a 'style' key.
 * In order to make css variables work without rewriting the every element we released so far,
 * we keep the same data structure for backward compatibility.
 */
export function extractVariableId(maybeValue: unknown) {
  // Try and extract nested value for unit values
  const maybeCssVariable = pipe(
    maybeValue,
    UnitInputValueV.decode,
    fold(
      () => maybeValue,
      unit => unit?.style
    )
  );

  if (typeof maybeCssVariable !== "string") return "";
  if (!maybeCssVariable.startsWith("{var-")) return "";

  return maybeCssVariable.replace("{var-", "").replace("}", "");
}

export function returnCssVariableOrNothing(maybeValue: unknown) {
  const maybeCssVariable = pipe(
    maybeValue,
    UnitInputValueV.decode,
    fold(
      () => maybeValue,
      unit => unit?.style
    )
  );

  return typeof maybeCssVariable === "string" ? maybeCssVariable : "";
}

export function getGlobalVariableByCssName(
  cssVariable: string
): Variable | undefined {
  const variables = getVariables();
  const name = extractVariableName(cssVariable);
  return variables.find(variable => variable.cssVariableName === name);
}

export function getGlobalVariableById(id: string) {
  const variables = getVariables();
  return variables.find(variable => variable.id === id);
}

export function createVariableName(str: string, suffix?: string | number) {
  const append = suffix ? `-${suffix}` : "";
  const slug = slugify(str, { lower: true });
  return `${slug}${append}`;
}

export function createCssVariable(variable: Variable) {
  if (variable.fallback) {
    return `var(--${variable.cssVariableName}, ${variable.fallback})`;
  }
  return `var(--${variable.cssVariableName})`;
}

export function getUniqueNameWithSuffix(newName: string, namesArray: string[]) {
  let count = 1;
  while (namesArray.includes(`${newName} #${count}`)) {
    count++;
  }
  return `${newName} #${count}`;
}

export function getVariableTemplates(labels: string[], collection = "Default") {
  const colorLabel = getUniqueNameWithSuffix("Color", labels);
  const numberLabel = getUniqueNameWithSuffix("Number", labels);
  const unitLabel = getUniqueNameWithSuffix("Unit", labels);
  const fontLabel = getUniqueNameWithSuffix("Font", labels);
  const imgLabel = getUniqueNameWithSuffix("Image", labels);

  return [
    {
      title: "Color",
      icon: "PaletteIcon",
      value: {
        id: "{uuid}",
        type: "color",
        label: colorLabel,
        cssVariableName: createVariableName(colorLabel),
        collection
      }
    },
    {
      title: "Number",
      icon: "HashtagIcon",
      value: {
        id: "{uuid}",
        type: "number",
        label: numberLabel,
        cssVariableName: createVariableName(numberLabel),
        collection
      }
    },
    {
      title: "Unit",
      icon: "UpRightAndDownLeftFromCenter",
      value: {
        id: "{uuid}",
        type: "unit",
        label: unitLabel,
        cssVariableName: createVariableName(unitLabel),
        collection
      }
    },
    {
      title: "Font Family",
      icon: "TextIcon",
      value: {
        id: "{uuid}",
        type: "font_family",
        label: fontLabel,
        cssVariableName: createVariableName(fontLabel),
        collection
      }
    },
    {
      title: "Image URL",
      icon: "ImageIcon",
      value: {
        id: "{uuid}",
        type: "image_url",
        label: imgLabel,
        cssVariableName: createVariableName(imgLabel),
        collection
      }
    }
  ];
}

export function getInputForVariableType(type: VariableType) {
  const inputTypeMap: Record<VariableType, string> = {
    number: "NumberInput",
    color: "ColorInput",
    unit: "UnitInput",
    font_family: "FontFamilyInput",
    text: "TextInput",
    image_url: "TextInput"
  };

  return inputTypeMap[type] || "TextInput";
}

export function parseControlTypeToVariableType(type: string): VariableType {
  const controlTypeMap: Record<string, VariableType> = {
    select: "text"
  };

  return controlTypeMap[type] || type;
}

export function getAttributesForVariableType(type: VariableType) {
  const attributesMap: Record<VariableType, Record<string, unknown>> = {
    number: {},
    color: {
      colorOptions: {
        type: "solidAndGradient"
      },
      showGlobal: false
    },
    unit: {
      expanded: true
    },
    font_family: {},
    text: {},
    image_url: {}
  };

  return attributesMap[type];
}

export function controlTypeToVariableType(control: Control) {
  const mapping: Partial<Record<ControlTypes, string>> = {
    "button-bar": "text",
    media: "image_url"
  };

  return mapping[control.type] || control.type.replace("-", "_");
}

export function doesControlAcceptVariable(control: Control) {
  if (control.disableVariable) {
    return false;
  }

  const type = controlTypeToVariableType(control);

  // One-off controls
  if (control.slug === "font_weight") {
    return true;
  }

  return [
    // "text",
    "number",
    "color",
    "font_family",
    "unit",
    "image_url"
  ].includes(type);
}

/**
 * Certain controls only accept solid or gradient colors
 */
export function maybeFilterColorVariable(
  variable: Variable,
  colorType?: ColorPickerType
) {
  if (variable.type !== "color") return true;

  const isGradient = isValueAGradient(variable.value);

  if (colorType === "gradientOnly") return isGradient;
  if (colorType === "solidAndGradient") return true;

  // Default to solidOnly
  return !isGradient;
}

export function filterVariablesByQuery(variables: Variable[], query?: string) {
  if (!query) return variables;

  const fuse = new Fuse(variables, {
    keys: ["label"],
    threshold: 0.2
  });

  return fuse.search(query.trim()).map(r => r.item);
}

export function getElementOverrideVariables(element: TreeNode): Variable[] {
  const variables =
    lodash.get(
      element,
      "data.properties.settings.override_variables.override_variables"
    ) || [];

  return variables.map((variable: Variable) => ({
    ...variable,
    local: true
  }));
}

export function getActiveElementOverrideVariables() {
  const el = uiStore.activeElement;
  if (!el) return [];

  return getElementOverrideVariables(el);
}

export function replaceVariablesInProperties(
  properties: Record<string, any>
): Record<string, any> {
  const variables = getVariables();
  for (const key in properties) {
    const value = properties[key];

    if (Array.isArray(value) || typeof value === "object") {
      properties[key] = replaceVariablesInProperties(value);
    } else if (typeof value === "string" && value.startsWith("{var-")) {
      const id = value.replace("{var-", "").replace("}", "");
      const variable = variables.find(item => item.id === id);

      if (variable) {
        properties[key] = `var(--${variable.cssVariableName})`;
      } else {
        properties[key] = null;
      }
    }
  }

  return properties;
}

export function replaceVariablesInString(
  input: string,
  variables: Variable[]
): string {
  return input.replace(/{var-(.*?)}/g, (match, id) => {
    const variable = variables.find(item => item.id === id);
    if (variable) {
      return `var(--${variable.cssVariableName})`;
    }
    return match; // Return the original match if variable not found
  });
}

function isParentSelector(selector: any): selector is OxySelector {
  return "children" in selector;
}

export function parseVariablesInSelector<
  T extends OxySelector | OxyNestedSelector
>(selector: T): T {
  if (selector.properties) {
    selector.properties = replaceVariablesInProperties(selector.properties);
  }

  if (isParentSelector(selector)) {
    selector.children = selector.children?.map(child => {
      return parseVariablesInSelector<OxyNestedSelector>(child);
    });
  }

  return selector;
}

// A variable could be pointed to another variable, so we need to resolve it
export function resolveGlobalVariableValueRecursively(value: unknown): unknown {
  while (isGlobalVariable(value)) {
    const globalVariable = getGlobalVariableFromInputValue(value);
    if (!globalVariable) break;
    value = globalVariable.value;
  }
  return value;
}

export function getVariablePreviewValue(
  variable: Variable,
  overrideVariable?: Variable
) {
  const value = resolveGlobalVariableValueRecursively(
    variableOrOverride(variable, overrideVariable)
  );

  if (value === null || value === undefined) return "";

  if (isDynamicShortcode(value)) {
    return "(dynamic)";
  }

  if (variable.type === "unit") {
    return pipe(
      value,
      UnitInputValueV.decode,
      fold(
        () => value,
        unit => unit?.style
      )
    );
  }

  if (variable.type === "font_family") {
    const foundVariable = configStore.fonts.find(font => font.slug === value);
    return foundVariable?.label;
  }

  if (variable.type === "color") {
    return pipe(
      value,
      GradientColorV.decode,
      fold(
        () => value,
        gradient => gradient.type
      )
    );
  }

  return value;
}

export function variableOrOverride(
  variable: Variable,
  overrideVariable?: Variable
) {
  return isVariableEmpty(overrideVariable)
    ? variable.value
    : overrideVariable?.value;
}

export function isVariableEmpty(variable?: Variable) {
  if (!variable) return true;
  return variable.value == null || variable.value === undefined;
}
